import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Typography,
  Paper,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  IconButton,
} from "@mui/material";
import {
  StarBorder as StarBorderIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import styled from "@emotion/styled";
import axios from "axios";
import PDFViewer from "./PDFViewer";
import MeetingNotes from "./MeetingNotes";
import useCarnivalWebSocket from "hooks/useCarnivalWebSocket";
import useUserId from "hooks/useUserId";
import { useTranslation } from "react-i18next";

const CVViewer = ({ urls }) => {
  if (!urls || urls.length === 0) return null;
  return <PDFViewer urls={urls} />;
};

// Professional WebSocket-based Carnival Video Call Component
const CarnivalVideoCall = ({ carnivalId }) => {
  const [candidates, setCandidates] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentCV, setCurrentCV] = useState([]);
  const [activeTab, setActiveTab] = useState("cv");
  const [isStarred, setIsStarred] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const { userId: userIdObject } = useUserId();
  const { t } = useTranslation();

  // Extract the actual user ID number
  const actualUserId = userIdObject?.userId || userIdObject?.id || userIdObject;

  // Stable function to fetch candidates - only called initially and via WebSocket
  const fetchWaitingCandidates = useCallback(async () => {
    try {
      const response = await axios.get(
        `${apiUrl}opportunities/api/interview-rooms/?carnival_id=${carnivalId}&status=waiting`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
          withCredentials: true,
          credentials: "include",
        }
      );

      const candidatesData = response.data.results || response.data;
      const candidatesList = Array.isArray(candidatesData)
        ? candidatesData
        : [];

      // Filter out completed interviews from frontend display
      const activeCandidates = candidatesList.filter((candidate) => {
        const isCompleted = candidate.is_completed || candidate.completed;
        return !isCompleted;
      });

      setCandidates(activeCandidates);
      setLoading(false);
    } catch (error) {
      setCandidates([]);
      setLoading(false);
    }
  }, [apiUrl, carnivalId]);

  // Stable WebSocket event handlers - prevent reconnections
  const handleParticipantUpdate = useCallback(
    (data) => {
      fetchWaitingCandidates();
    },
    [fetchWaitingCandidates]
  );

  const handleQueueUpdate = useCallback(
    (data) => {
      fetchWaitingCandidates();
    },
    [fetchWaitingCandidates]
  );

  const handleStatusChange = useCallback((data) => {
    if (data.is_archived) {
      setCandidates([]);
      setCurrentRoom(null);
    }
  }, []);

  // WebSocket connection - stable configuration
  const { isConnected } = useCarnivalWebSocket(carnivalId, actualUserId, {
    onParticipantUpdate: handleParticipantUpdate,
    onQueueUpdate: handleQueueUpdate,
    onStatusChange: handleStatusChange,
    debug: false, // Clean logs
  });

  const GoldenStarWrapper = styled(IconButton)(({ theme }) => ({
    position: "relative",
    "&:hover": {
      backgroundColor: "transparent",
      "& .star-icon": {
        filter: "drop-shadow(0 0 4px rgba(255, 215, 0, 0.8))",
      },
    },
    "& .sparkle": {
      position: "absolute",
      width: "100%",
      height: "100%",
      animation: `${sparkleEffect} 0.6s ease-out`,
      "&:after": {
        content: '""',
        position: "absolute",
        width: "8px",
        height: "8px",
        background: "radial-gradient(#fff, rgba(255, 215, 0, 0.8))",
        borderRadius: "50%",
        top: "30%",
        left: "30%",
      },
    },
  }));
  const goldenPulse = `
@keyframes goldenPulse {
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
}
`;

  const sparkleEffect = `
@keyframes sparkle {
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(0); opacity: 0; }
}
`;

  useEffect(() => {
    if (currentRoom?.is_starred !== undefined) {
      setIsStarred(currentRoom.is_starred);
    }
  }, [currentRoom]);

  const handleStarToggle = async () => {
    if (!currentRoom?.id) return;

    const newStarredState = !isStarred;
    setIsStarred(newStarredState);

    try {
      await axios.put(
        `${apiUrl}opportunities/api/interview-rooms/${currentRoom.id}/toggle-star/`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
          withCredentials: true,
          credentials: "include",
        }
      );
    } catch (error) {
      console.error("Error toggling star:", error);
      setIsStarred(!newStarredState);
    }
  };

  // Initial data fetch and WebSocket connection
  useEffect(() => {
    fetchWaitingCandidates();
  }, [fetchWaitingCandidates, isConnected, actualUserId, carnivalId]);

  useEffect(() => {
    if (currentRoom?.id) {
      fetchCandidateCV(currentRoom.id);
    } else {
      setCurrentCV([]);
    }
  }, [currentRoom]);

  const getNextCandidate = () => {
    const currentIndex = candidates.findIndex((c) => c.id === currentRoom?.id);
    if (currentIndex < candidates.length - 1) {
      return candidates[currentIndex + 1];
    }
    return null;
  };

  const handleMarkAsCompleted = async (roomId) => {
    try {
      // Check if interview is already completed
      const room = candidates.find((c) => c.id === roomId);
      if (room && room.completed) {
        setCurrentRoom(null);
        return;
      }

      await axios.post(
        `${apiUrl}opportunities/api/interview-rooms/${roomId}/complete_interview/`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
          withCredentials: true,
          credentials: "include",
        }
      );

      setCurrentRoom(null);
    } catch (error) {
      // If it's already completed, that's fine
      if (
        error.response?.status === 400 &&
        error.response?.data?.error?.includes("already")
      ) {
        setCurrentRoom(null);
      }
    }
  };

  const handleNext = async () => {
    if (currentRoom) {
      const nextCandidate = getNextCandidate();
      await handleMarkAsCompleted(currentRoom.id);

      if (nextCandidate) {
        await startInterview(nextCandidate);
      } else {
        // Force refresh the candidate list to ensure completed interview is removed
        await fetchWaitingCandidates();
      }
    }
  };

  const fetchCandidateCV = async (roomId) => {
    try {
      const response = await axios.get(
        `${apiUrl}opportunities/api/candidates/${roomId}/cv/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
          responseType: "json",
        }
      );

      setCurrentCV(response.data.pages || []);
    } catch (error) {
      setCurrentCV([]);
    }
  };

  const startInterview = async (room) => {
    try {
      await axios.post(
        `${apiUrl}opportunities/api/interview-rooms/${room.id}/start_interview/`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
          withCredentials: true,
          credentials: "include",
        }
      );

      setCurrentRoom(room);
    } catch (error) {
      // If interview already started, just set the current room
      if (
        error.response?.status === 400 &&
        error.response?.data?.error?.includes("already started")
      ) {
        setCurrentRoom(room);
      } else {
        // For other errors, still set the room
        setCurrentRoom(room);
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        bgcolor: "background.default",
      }}
    >
      {/* Horizontal Waiting List */}
      <Paper
        elevation={2}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          overflow: "auto",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            px: 1,
            py: 0.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle1">
            {t("carnivals.waiting")} ({candidates.length})
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isConnected ? (
              <>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "success.main",
                    animation: "pulse 2s infinite",
                  }}
                  title={t("carnivals.realTimeUpdatesActive")}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "success.main", fontSize: "0.7rem" }}
                >
                  {t("carnivals.live")}
                </Typography>
              </>
            ) : (
              <Typography
                variant="caption"
                sx={{ color: "warning.main", fontSize: "0.7rem" }}
              >
                {t("carnivals.polling")}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ overflow: "auto", display: "flex" }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                p: 1,
                width: "100%",
              }}
            >
              <CircularProgress size={20} />
            </Box>
          ) : (
            <List dense sx={{ display: "flex", p: 0 }}>
              {candidates.map((room) => (
                <ListItem
                  key={room.id}
                  button
                  onClick={() => startInterview(room)}
                  selected={currentRoom?.id === room.id}
                  sx={{
                    minWidth: 150,
                    borderRight: "1px solid rgba(0, 0, 0, 0.06)",
                    bgcolor:
                      currentRoom?.id === room.id
                        ? "action.selected"
                        : "inherit",
                  }}
                >
                  <ListItemText
                    primary={room.candidate_name}
                    secondary={`Queue: ${room.queue_position}`}
                    sx={{
                      m: 0,
                      "& .MuiTypography-root": { fontSize: "0.875rem" },
                      "& .MuiTypography-secondary": { fontSize: "0.75rem" },
                    }}
                  />
                </ListItem>
              ))}
              {candidates.length === 0 && (
                <Typography
                  sx={{ p: 1, color: "text.secondary", fontSize: "0.875rem" }}
                >
                  {t("carnivals.noCandidatesWaiting")}
                </Typography>
              )}
            </List>
          )}
        </Box>
      </Paper>

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, display: "flex" }}>
        {currentRoom ? (
          <>
            {/* Video Call Section */}
            <Box sx={{ flexGrow: 1, p: 1, width: "55%" }}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1">
                      {t("carnivals.interview")}: {currentRoom.candidate_name}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {getNextCandidate() && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleNext}
                          sx={{
                            background:
                              "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
                            "&:hover": {
                              background:
                                "linear-gradient(45deg, #0010a3 30%, #1e3a8a 90%)",
                              transform: "translateY(-1px)",
                            },
                            transition: "all 0.2s ease-in-out",
                          }}
                        >
                          {t("carnivals.next")}
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={async () => {
                          await handleMarkAsCompleted(currentRoom.id);
                          // Force refresh to ensure completed interview is removed from display
                          await fetchWaitingCandidates();
                        }}
                        sx={{
                          borderColor: "#0017E7",
                          color: "#0017E7",
                          "&:hover": {
                            borderColor: "#0010a3",
                            backgroundColor: "rgba(0, 23, 231, 0.04)",
                          },
                        }}
                      >
                        {t("carnivals.complete")}
                      </Button>
                    </Box>
                  </Box>
                  <Box sx={{ height: "calc(100vh - 170px)" }}>
                    <iframe
                      src={currentRoom.room_link}
                      style={{ width: "100%", height: "100%", border: "none" }}
                      allow="camera; microphone; display-capture; fullscreen; clipboard-read; clipboard-write"
                      title="Interview Room"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* CV & Notes Section */}
            <Box sx={{ width: "45%", ml: 1, mr: 1, mt: 1, mb: 1 }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{ borderBottom: 1, borderColor: "divider" }}
              >
                <Tab label={t("carnivals.cv")} value="cv" />
                <Tab label={t("carnivals.notes")} value="notes" />
              </Tabs>

              {activeTab === "cv" ? (
                <Card
                  sx={{
                    height: "calc(100vh - 170px)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <GoldenStarWrapper
                    onClick={handleStarToggle}
                    size="small"
                    sx={{
                      ml: "auto",
                      "& .MuiSvgIcon-root": {
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      },
                    }}
                  >
                    <style>{goldenPulse}</style>
                    {isStarred ? (
                      <>
                        <div className="sparkle"></div>
                        <StarIcon
                          className="star-icon"
                          sx={{
                            fontSize: "28px",
                            color: "#FFD700",
                            animation: `${goldenPulse} 2s infinite`,
                            filter:
                              "drop-shadow(0 0 2px rgba(255, 215, 0, 0.5))",
                          }}
                        />
                      </>
                    ) : (
                      <StarBorderIcon
                        className="star-icon"
                        sx={{
                          fontSize: "28px",
                          color: "#666",
                          "&:hover": {
                            color: "#FFD700",
                            transform: "scale(1.1)",
                          },
                        }}
                      />
                    )}
                  </GoldenStarWrapper>
                  <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                    {currentCV.length > 0 ? (
                      <CVViewer urls={currentCV} />
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                        }}
                      >
                        <Typography color="textSecondary">
                          {t("carnivals.noCvAvailable")}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Card>
              ) : (
                <Box sx={{ height: "calc(100vh - 170px)" }}>
                  <MeetingNotes currentRoom={currentRoom} />
                </Box>
              )}
            </Box>
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Typography variant="subtitle1" color="textSecondary">
              {t("carnivals.selectCandidateToStart")}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CarnivalVideoCall;
